///////////////////////////////////////////////////////////////////////////////
// Shared "bottom of page" trust section — payment rails, trust badge, and
// product FAQ. Every catalog page (gifts, recharge, eSIM) renders the same
// <PageFooter faq={...} seo={...} /> here rather than assembling its own —
// add a new product page's footer here, not by copying markup elsewhere.
///////////////////////////////////////////////////////////////////////////////
import React from 'react';
import { ST, BUILD_VERSION, PreferencesTrigger, useT } from './Chrome';
import { RequestProductLink } from './RequestProduct';

// /st is served with a year-long immutable cache, so a file first requested
// during a deploy can have a miss cached against it and stay broken. Stamping
// the build gives each release its own key.
const ASSET_V = BUILD_VERSION && BUILD_VERSION !== 'dev' ? `?v=${encodeURIComponent(BUILD_VERSION)}` : '';

// Substitute {name}/{amount}/etc. placeholders in a translated string — same
// helper duplicated per-file elsewhere (Account.jsx, TopupPay.jsx, EsimLanding.jsx).
function fill(s, vars) {
  return String(s).replace(/\{(\w+)\}/g, (m, k) => (vars[k] != null ? vars[k] : m));
}

// Every payment rail we accept, in one box, with a single line about the fee.
export const PAY_RAILS = [
  'visa.png', 'master.png', 'amex.png', 'jcb.png',
  'ethereum.svg', 'base.svg', 'arbitrum.svg',
  'kbank.png', 'scb.png', 'truemoney.svg', 'linepay.svg',
  'paymaya-logo.svg', 'bpi-logo.svg', 'ubp-logo.svg', 'chinabank-logo.svg',
  'rcbc-logo.svg', 'grabpay-logo.svg', 'shopeepay-logo.svg',
];

export function PayBox() {
  return (
    <section className="paysec">
      <h2 className="cg-h2">How to pay</h2>
      <div className="paybox">
        <div className="paybox-logos">
          {PAY_RAILS.map((f) => (
            // no lazy loading: width:auto collapses the box to ~2px before the
            // intrinsic size is known, so the browser never starts the fetch
            <img key={f} src={`${ST}/images/payment-icons/${f}${ASSET_V}`} alt="" width="44" height="28" />
          ))}
        </div>
        <p className="paybox-note">
          <b>No hidden fees.</b> Card and USDC add a small processing fee, your MobileTopup
          balance adds none — the exact total is shown before you confirm.
        </p>
      </div>
    </section>
  );
}

export function TrustBadge() {
  return (
    <div className="railcard trust-badge">
      <h4>Trusted Since 2009</h4>
      <div className="trust-stars" aria-hidden="true">
        {[0, 1, 2, 3, 4].map((i) => (
          <svg key={i} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12 2.5l2.9 6.3 6.9.7-5.2 4.7 1.5 6.8L12 17.6l-6.1 3.4 1.5-6.8L2.2 9.5l6.9-.7z" />
          </svg>
        ))}
      </div>
    </div>
  );
}

// FAQ accordion + optional SEO paragraph. Same shape Landing.jsx's
// LandingSections used — kept here so every page's FAQ renders identically.
export function FaqSection({ faq, seo, heading = 'Frequently asked questions' }) {
  return (
    <>
      {faq && faq.length > 0 && (
        <section className="faq">
          <h2 className="landing-h2">{heading}</h2>
          {faq.map(([q, a]) => (
            <details key={q}><summary>{q}</summary><p>{a}</p></details>
          ))}
        </section>
      )}
      {seo && <p className="seo-copy">{seo}</p>}
    </>
  );
}

// The one footer every product page renders — full payment rails, trust
// badge, product FAQ. To add a new product page's footer, call this with
// that product's FAQ copy; to change what EVERY page's footer looks like,
// change it here once.
export function PageFooter({ faq, seo }) {
  return (
    <>
      <PayBox />
      <TrustBadge />
      <FaqSection faq={faq} seo={seo} />
    </>
  );
}

// Gift-card FAQ — plain English, no per-country variation (the gift/game
// catalog doesn't change in kind by country the way airtime carriers do).
export const GIFT_FAQ = [
  ['How fast will I get my gift card code?', 'Your code (or redemption link) is delivered by email within minutes of payment — usually seconds. Check your spam folder if it hasn’t arrived after 15 minutes.'],
  ['Can I buy a gift card for someone else?', 'Yes — enter the recipient’s name at checkout and forward them the email, or redeem it yourself. No account or phone number is required either way.'],
  ['Are gift cards refundable?', 'No — like any digital voucher, gift cards are non-refundable and can’t be exchanged for cash once the code has been delivered. Double-check the brand and amount before paying.'],
  ['Which countries and brands are available?', 'Coverage varies by country — pick your country above to see the brands we carry there, from major retailers and streaming services to game credit and prepaid cards.'],
  ['What payment methods do you accept?', 'International credit and debit cards (with 3-D Secure verification), USDC, and your MobileTopup balance.'],
  ['Do gift cards expire?', 'Most don’t, but some brands set a claim-by date on their vouchers — this is noted on the product page and in your confirmation email when it applies.'],
];

///////////////////////////////////////////////////////////////////////////////
// Site footer — the one footer for every page (rolled out site-wide
// 2026-09-16, replacing the old plain Footer() in Chrome.jsx). Support /
// Products / Company / Resources columns, real payment-rail and app-store
// badge art, the header's language/currency picker reused here, and the
// Vivid Productions registered-office line.
///////////////////////////////////////////////////////////////////////////////
const FOOTER_PAY_RAILS = [
  { file: 'visa.png', label: 'Visa', w: 128, h: 80 },
  { file: 'master.png', label: 'Mastercard', w: 128, h: 80 },
  { file: 'amex.png', label: 'American Express', w: 128, h: 80 },
  { file: 'jcb.png', label: 'JCB', w: 128, h: 80 },
  { file: 'apple-pay.svg', label: 'Apple Pay', w: 166, h: 106 },
  { file: 'ethereum.svg', label: 'Ethereum', w: 32, h: 32 },
  { file: 'base.svg', label: 'Base', w: 32, h: 32 },
  { file: 'arbitrum.svg', label: 'Arbitrum', w: 32, h: 32 },
  { file: 'kbank.png', label: 'Kasikorn Bank', w: 60, h: 60 },
  { file: 'scb.png', label: 'SCB', w: 60, h: 60 },
  { file: 'truemoney.svg', label: 'TrueMoney', w: 32, h: 32 },
  { file: 'linepay.svg', label: 'LINE Pay', w: 94, h: 99 },
  { file: 'paymaya-logo.svg', label: 'Maya', w: 90, h: 60 },
  { file: 'bpi-logo.svg', label: 'BPI', w: 90, h: 60 },
  { file: 'ubp-logo.svg', label: 'UnionBank', w: 90, h: 60 },
  { file: 'chinabank-logo.svg', label: 'China Bank', w: 90, h: 60 },
  { file: 'rcbc-logo.svg', label: 'RCBC', w: 90, h: 90 },
  { file: 'grabpay-logo.svg', label: 'GrabPay', w: 90, h: 60 },
  { file: 'shopeepay-logo.svg', label: 'ShopeePay', w: 90, h: 60 },
];

// "Ask an AI" links — pre-fill the same prompt (translated per language,
// footer_ai_query) on each assistant's own site, asking it to summarize
// MobileTopup from mobiletopup.com as the primary source. Answer-engine
// discovery, same idea as the payment-rail/app-badge rows: meet people
// where they already are. A function of t() (not a module-level constant)
// so the query re-encodes in the visitor's own language.
function aiSummaryLinks(t) {
  const q = encodeURIComponent(t('footer_ai_query'));
  return [
    { label: 'ChatGPT', icon: 'openai.svg', href: `https://chatgpt.com/?q=${q}` },
    // Gemini goes through Google Search's AI Mode (udm=50) rather than a
    // standalone Gemini app URL — that's what actually renders a Gemini
    // answer linked from a plain URL.
    { label: 'Gemini', icon: 'gemini.png', href: `https://www.google.com/search?q=${q}&udm=50` },
    { label: 'Grok', icon: 'grok.svg', href: `https://grok.com/?q=${q}` },
    { label: 'Perplexity', icon: 'perplexity.svg', href: `https://www.perplexity.ai/search?q=${q}` },
  ];
}

const FOOTER_SOCIAL = [
  { href: 'https://www.facebook.com/thaiprepaidcard', label: 'Facebook', d: 'M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5 3.66 9.15 8.44 9.94v-7.03H7.9v-2.91h2.54V9.85c0-2.51 1.49-3.9 3.77-3.9 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.78-1.63 1.57v1.88h2.78l-.44 2.91h-2.34V22c4.78-.79 8.44-4.94 8.44-9.94z' },
  { href: 'https://x.com/thaiprepaidcard', label: 'X (Twitter)', d: 'M18.9 2H22l-7.6 8.7L23.3 22h-7l-5.5-7.2L4.5 22H1.4l8.2-9.3L1 2h7.2l5 6.6L18.9 2zm-1.2 18h1.7L7.4 4h-1.8l12.1 16z' },
  { href: 'http://m.me/thaiprepaidcard', label: 'Messenger', d: 'M12 2C6.5 2 2 6.14 2 11.5c0 3.06 1.46 5.79 3.75 7.57V22l3.44-1.89c.9.25 1.85.39 2.81.39 5.5 0 10-4.14 10-9.5S17.5 2 12 2zm1.01 12.79l-2.55-2.72-4.98 2.72 5.48-5.82 2.61 2.72 4.92-2.72-5.48 5.82z' },
  { href: 'https://t.me/mobiletopup_bot', label: 'Telegram', d: 'M9.99 15.2l-.38 4.08c.55 0 .78-.24 1.07-.52l2.58-2.45 5.35 3.9c.98.55 1.68.26 1.93-.9L23.9 4.9c.33-1.4-.5-1.96-1.44-1.6L1.14 11.2c-1.37.53-1.35 1.3-.24 1.65l5.6 1.75L19.7 6.9c.6-.4 1.15-.17.7.23L9.99 15.2z' },
  { href: 'https://wa.me/66926200610', label: 'WhatsApp', d: 'M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 004.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91C21.96 6.45 17.5 2 12.04 2zm5.83 14.17c-.25.7-1.25 1.29-1.98 1.44-.52.11-1.19.2-3.47-.72-2.9-1.19-4.77-4.14-4.92-4.33-.14-.19-1.18-1.57-1.18-3 0-1.43.75-2.13 1.01-2.42.27-.29.58-.36.78-.36h.56c.18 0 .42-.03.65.5.25.6.85 2.03.92 2.18.07.14.12.31.02.5-.09.19-.14.3-.28.46-.14.16-.29.36-.42.48-.14.13-.28.28-.12.55.16.27.71 1.17 1.53 1.9 1.05.94 1.94 1.24 2.21 1.38.27.14.43.12.58-.05.16-.17.68-.79.86-1.06.18-.27.36-.22.6-.13.25.09 1.6.76 1.87.9.27.13.45.2.52.31.07.12.07.68-.18 1.38z' },
];

export function SiteFooterV2({ currency, onCurrency }) {
  const t = useT();
  return (
    <footer className="site-foot">
      <div className="site-foot-pay">
        <div className="site-foot-pay-logos">
          {FOOTER_PAY_RAILS.map((r) => (
            <img
              key={r.file} src={`${ST}/images/payment-icons/${r.file}${ASSET_V}`}
              width={r.w} height={r.h} loading="lazy" decoding="async" alt={r.label} title={r.label}
            />
          ))}
        </div>
      </div>

      <div className="site-foot-cols">
        <div className="site-foot-col site-foot-brand">
          <a href="/" aria-label={t('footer_logo_home')}>
            <img
              className="site-foot-logo" src={`${ST}/images/2/logo-dark.png${ASSET_V}`}
              width="340" height="84" loading="lazy" decoding="async" alt="MobileTopup.com"
            />
          </a>
          <div className="site-foot-apps">
            <a href="https://apps.apple.com/app/mobiletopup-com/id6456984604" target="_blank" rel="noopener noreferrer">
              <img src={`${ST}/images/2/apple-store.svg${ASSET_V}`} width="120" height="40" loading="lazy" decoding="async" alt={t('footer_app_store_alt')} />
            </a>
            <a href="https://play.google.com/store/apps/details?id=com.mobiletopup.mobiletopup1" target="_blank" rel="noopener noreferrer">
              <img src={`${ST}/images/2/google-play.png${ASSET_V}`} width="572" height="176" loading="lazy" decoding="async" alt={t('footer_google_play_alt')} />
            </a>
          </div>
          {currency && onCurrency && <PreferencesTrigger currency={currency} onCurrency={onCurrency} />}
        </div>

        <nav className="site-foot-col" aria-label={t('footer_support')}>
          <h4>{t('footer_support')}</h4>
          <a href="/support">{t('footer_help')}</a>
          <a href="/contact">{t('footer_contact_form')}</a>
          <a href="/refund-policy">{t('footer_refund_policy')}</a>
          <RequestProductLink label="Request a product" className="request-product-link" />
        </nav>
        <nav className="site-foot-col" aria-label={t('footer_products')}>
          <h4>{t('footer_products')}</h4>
          <a href="/">{t('nav_esim_data')}</a>
          <a href="/recharge">{t('nav_mobile_topup')}</a>
          <a href="/gifts">{t('nav_gifts')}</a>
          <a href="/l/keep-thai-sim-active">{t('footer_thai_sim_validity')}</a>
          <a href="/l/top-me-up">{t('footer_top_me_up')}</a>
        </nav>
        <nav className="site-foot-col" aria-label={t('footer_company')}>
          <h4>{t('footer_company')}</h4>
          <a href="/about">{t('footer_about')}</a>
        </nav>
        <nav className="site-foot-col" aria-label={t('footer_articles')}>
          <h4>{t('footer_articles')}</h4>
          <a href="/lucky-number">{t('lucky_title')}</a>
          <a href="/topup/thailand">{t('footer_thai_mobile_credit')}</a>
          <a href="/countries">{t('countries_list')}</a>
          <a href="/agents">{t('footer_ai_agents')}</a>
        </nav>
      </div>

      <div className="site-foot-divider" />

      <div className="site-foot-bottom">
        <div className="site-foot-legal">
          <span>&copy; 2009&ndash;{new Date().getFullYear()} MobileTopup.com</span>
          <span className="sep">&middot;</span>
          <a href="/terms">{t('footer_terms')}</a>
          <span className="sep">&middot;</span>
          <a href="/privacy">{t('footer_privacy')}</a>
          <span className="sep">&middot;</span>
          <a href="/cookie-privacy">{t('footer_privacy_cookie')}</a>
          <span className="sep">&middot;</span>
          <a href="/security">{t('footer_security')}</a>
          <span className="sep">&middot;</span>
          <a href="/refund-policy">{t('footer_refund_policy')}</a>
        </div>
        <div className="site-foot-right">
          <div className="site-foot-ai">
            <span className="site-foot-ai-label">{t('footer_ai_summary')}</span>
            <div className="site-foot-ai-links">
              {aiSummaryLinks(t).map((l) => {
                const askLabel = fill(t('footer_ask_ai'), { service: l.label });
                return (
                  <a
                    key={l.label} href={l.href} target="_blank" rel="noopener noreferrer"
                    title={askLabel} aria-label={askLabel}
                  >
                    <img src={`${ST}/images/ai-icons/${l.icon}${ASSET_V}`} width="22" height="22" loading="lazy" decoding="async" alt="" />
                  </a>
                );
              })}
            </div>
          </div>
          <div className="site-foot-social">
            {FOOTER_SOCIAL.map((s) => (
              <a key={s.label} href={s.href} aria-label={s.label} target="_blank" rel="noopener noreferrer">
                <svg viewBox="0 0 24 24"><path d={s.d} /></svg>
              </a>
            ))}
          </div>
        </div>
      </div>

      <p className="site-foot-registration">
        {fill(t('footer_registration_owner'), { company: 'Vivid Productions, LLC' })}
        {' '}
        {fill(t('footer_registration_address'), { address: '100 N Howard St Ste R, Spokane, WA 99201-0508, United States.' })}
      </p>
    </footer>
  );
}
